import { HttpTransportType, HubConnection, HubConnectionState } from '@microsoft/signalr';
import { ReactNode, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { LogLevel } from 'react-signalr';
import { retrieveUserFromStorage } from '../hooks/useSessionUser';
import { RootState } from '../store';
import EnvWindow from '../utils/env';
import { Context, Hub } from 'react-signalr/lib/signalr/types';

interface SignalRCustomProviderProps<H extends Hub> {
  children: ReactNode;
  context: Context<H>;
  url: string;
  disabled?: boolean;
  onConnectionChanged?: (connected: boolean) => void;
}
export const SignalRCustomProvider = <H extends Hub>({
  children,
  context,
  url,
  disabled,
  onConnectionChanged,
}: SignalRCustomProviderProps<H>): JSX.Element => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userRef = useRef(user);

  const hasValidToken = (): boolean => {
    const storedUser = retrieveUserFromStorage();
    return !!(storedUser && storedUser.access_token);
  };

  const isConnectEnabled = !disabled && hasValidToken();

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const accessTokenFactory = () => {
    const token = retrieveUserFromStorage()?.access_token || '';

    if (!token) {
      console.warn('SignalR: No access token available for connection');
      return '';
    }

    return token;
  };

  const devEnvProps = EnvWindow.REACT_APP_DXC_ENV
    ? {
        logger: 2,
        onOpen: (conn: HubConnection) => {
          // Check if connection is established
          if (conn.state === HubConnectionState.Connected) {
            onConnectionChanged && onConnectionChanged(true);
            console.log('SignalR: Connection opened with ID: ', conn.connectionId);
          } else {
            console.warn('SignalR: Connection opened but not fully established. State: ' + conn.state);
          }
        },
        onClosed: (e?: Error) => {
          onConnectionChanged && onConnectionChanged(false);
          console.log('SignalR: Closed WS connection.', e?.message || 'No error');
        },
        onError: (e?: Error) => {
          console.error('SignalR: Error:', e?.message || 'Unknown error');
          return Promise.resolve();
        },
        onReconnect: (conn: HubConnection) => {
          console.log('SignalR: Reconnected with ID: ', conn.connectionId);
        },
      }
    : {
        onOpen: () => {
          onConnectionChanged && onConnectionChanged(true);
        },
        onClosed: () => {
          onConnectionChanged && onConnectionChanged(false);
        },
      };

  const signalROptions: any = {
    connectEnabled: isConnectEnabled,
    url,
    accessTokenFactory,
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
    keepAliveIntervalInMilliseconds: 15000,
    serverTimeoutInMilliseconds: 30000,
    ...devEnvProps,
  };

  return <context.Provider {...signalROptions}>{children}</context.Provider>;
};
